import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LifelineApplicationDto,
  LifelineListItemDto,
  LifelinePlanOption,
  LifelineRemovalResultDto,
  Paged,
} from '@app/shared';
import { api } from '../../lib/api';
import type { Query } from '../../lib/api';

/** Lifeline applications (PC1164 to PC1168) server calls. */

export const BASE = '/lifeline/applications';
const oneKey = (id: string | undefined) => ['lifeline', 'one', id] as const;

export const useLifelineApplications = (params: Query) =>
  useQuery({
    queryKey: ['lifeline', 'list', params],
    queryFn: () => api.get<Paged<LifelineListItemDto>>(BASE, params),
    placeholderData: (prev) => prev,
  });

export const useLifelineApplication = (id: string | undefined) =>
  useQuery({
    queryKey: oneKey(id),
    queryFn: () => api.get<LifelineApplicationDto>(`${BASE}/${id}`),
    enabled: Boolean(id),
  });

export const useLifelinePlans = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: ['lifeline', 'plans', id],
    queryFn: () => api.get<{ data: LifelinePlanOption[] }>(`${BASE}/${id}/plans`),
    select: (r) => r.data,
    enabled,
  });

/** Every step's call answers with the application; the cache takes it as is. */
export function useLifelineStep<V>(call: (vars: V) => Promise<LifelineApplicationDto>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: async (app) => {
      queryClient.setQueryData(oneKey(app.id), app);
      await queryClient.invalidateQueries({ queryKey: ['lifeline', 'list'] });
    },
  });
}

export const useRemoveLifeline = (customerId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      api.post<LifelineRemovalResultDto>(`/lifeline/customers/${customerId}/remove`, { reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
};
